<script setup lang="ts">
import type { Room } from '~/shared/types/payload-types'
import { FLOOR_ORDER, type FloorValue } from '~/composables/use-floor-svgs'

const route = useRoute()
const { locale, t } = useI18n()

const runtimeConfig = useRuntimeConfig()

const previewSecret = runtimeConfig?.previewSecret || runtimeConfig?.public?.previewSecret
const isPreview = computed(() => route?.query?.previewSecret === previewSecret)

const { data, error } = await useAsyncData<RoomsPage>(
  () => `rooms-page-${locale.value}`,
  () => {
    return $fetch<RoomsPage>('/api/payload-gql', {
      method: 'POST',
      body: {
        query: GlobalRoomsPageQuery,
        variables: {
          locale: locale.value,
          isPreview: isPreview.value,
        },
        isPreview: isPreview.value,
      },
    })
  }
)

if (error.value) {
  throw createError({
    statusCode: 500,
    statusMessage: `Error fetching page`,
  })
}

usePageSeo({
  title: () => data.value?.title || 'Rooms',
})

type FloorGroup = { floor: FloorValue; rooms: Room[] }

// shared with /rooms/[slug] and preloaded on idle by plugins/preload-rooms.client.ts,
// so this usually resolves from useState without a request
const { rooms, loaded: roomsLoaded, load: loadRooms } = useRoomsData({ isPreview })
const { preload: preloadFloorSvgs } = useFloorSvgs()

onMounted(() => {
  // preview must always re-fetch, it can't be served from the visit cache
  loadRooms({ force: isPreview.value })
  preloadFloorSvgs()
})

const floorGroups = computed<FloorGroup[]>(() => {
  const groups = new Map<FloorValue, Room[]>()

  for (const room of rooms.value) {
    const floor = (room as any).floor as FloorValue | undefined
    if (!floor) continue
    if (!groups.has(floor)) groups.set(floor, [])
    groups.get(floor)!.push(room)
  }

  return FLOOR_ORDER
    .filter(floor => groups.has(floor))
    .map(floor => ({ floor, rooms: groups.get(floor)! }))
})

const pageNav = computed<PageNavigationItem[]>(() =>
  floorGroups.value.map(group => ({
    id: group.floor,
    title: t(`floorplan.floors.${group.floor}`),
  }))
)
</script>

<template>
  <page-layout>
    <template #header>
      <page-header
        :title="data?.title || 'Rooms'"
        :route="{
          name: 'rooms',
          title: data?.title,
          slug: data?.slug,
        }"
        :include-breadcrumbs="true"
        :page-nav="pageNav"
      />
    </template>

    <template #default>
      <pages-rooms
        v-if="data"
        :data="data"
        :floor-groups="floorGroups"
        :is-loading="!roomsLoaded"
      />

      <nuxt-page />
    </template>
  </page-layout>
</template>
